import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import { Grid, Slide } from '@material-ui/core';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TooltipItem
} from 'chart.js';
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import BarChartIcon from '@material-ui/icons/BarChart';

type OwnProps = {
    facilityId: number;
};

const mapState = (state: ApplicationState) => state.vitalsContext;

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof APIStore.actionCreators;
type FacilityAdmitTypeBarChartProps = OwnProps & StateProps & DispatchProps;

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const FacilityAdmitTypeBarChart = (props: FacilityAdmitTypeBarChartProps) => {
    const {
        PatientFacilityDrillDownStats,
        userPracticeCoordinatorProfile,
        userRefMdProfile,
        APIToken,
        requestCurrentUserCareCoordinatorProfile,
        requestPatientLocationDrillDownStatsForProvider,
    } = props;

    const [tokenState, setTokenState] = useState<APIStore.TokenType | undefined>();

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }
    }, [APIToken, tokenState]);

    useEffect(() => {
        if (userPracticeCoordinatorProfile) {
            const { isErrored, notFound, isLoaded } = userPracticeCoordinatorProfile;
            if ((!isErrored && !notFound && !isLoaded) && tokenState?.accessToken) {
                requestCurrentUserCareCoordinatorProfile(tokenState.accessToken);
            } else {
                if (isLoaded && tokenState?.accessToken) {
                    if (!PatientFacilityDrillDownStats.isLoaded && !PatientFacilityDrillDownStats.isLoading) {
                        requestPatientLocationDrillDownStatsForProvider(tokenState.accessToken, props.facilityId);
                    }
                }
            }
        }
    }, [tokenState, userPracticeCoordinatorProfile.coordinatorProfile]);

    if (!PatientFacilityDrillDownStats || PatientFacilityDrillDownStats.isLoading || !PatientFacilityDrillDownStats.isLoaded) {
        return (
            <Slide in timeout={1000} direction="right">
                <Grid container>
                    <Grid item md={4}>
                        <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled" />
                    </Grid>
                </Grid>
            </Slide>
        );
    }

    if (PatientFacilityDrillDownStats.isErrored) {
        return (<React.Fragment><p>Error Loading</p></React.Fragment>);
    }

    const admits: APIStore.AdmitTypeStatistics[] =
        PatientFacilityDrillDownStats.patientFacilityDrillDownCounts?.admitsByType ?? [];

    const labels = admits.map(val => val.admitType);
    const values = admits.map(val => val.totalAdmits);

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: "top" as const,
            },
            title: {
                display: true,
                text: "Admissions by Type",
            },
            tooltip: {
                callbacks: {
                    title: (items: TooltipItem<'bar'>[]) => items[0]?.label ?? '',
                    label: (item: TooltipItem<'bar'>) => ` Admissions: ${item.raw}`,
                },
            },
        },
    };

    const data = {
        labels,
        datasets: [
            {
                label: "Total Admits",
                data: values,
                backgroundColor: "rgba(82, 189, 228, 0.6)",
                borderColor: "rgba(82, 189, 228, 1)",
                borderWidth: 1,
            },
        ],
    };

    return (
        <React.Fragment>
            <Grid item xs={12} direction="column" container>
                <Grid item xs={12}>
                    <Bar options={options} data={data} />
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state: ApplicationState) => state.vitalsContext,
    APIStore.actionCreators
)(FacilityAdmitTypeBarChart as any);
